// Logica pura do controle de audio por participante: medicao de nivel e a
// maquina de estados do gate ("ouvir so quando falar").
//
// Fica separada do WebAudio de proposito — assim da para testar sem DOM.

// Faixa util de limiar em RMS (sinal normalizado -1..1). Fala normal fica
// entre 0.02 e 0.2; chiado de sala silenciosa fica abaixo de 0.005.
const LIMIAR_MIN = 0.004;  // sensibilidade alta: abre com quase qualquer som
const LIMIAR_MAX = 0.06;   // sensibilidade baixa: so abre com fala clara

// Depois que o nivel cai, o gate fica aberto mais um tempo. E isso que evita
// picotar o fim das palavras e as pausas curtas no meio de uma frase.
export const HOLD_MS = 400;

// Duracao do fade de volume ao abrir/fechar o gate. Rapido o suficiente para
// nao atrasar a fala, suave o suficiente para nao dar estalo.
export const DURACAO_FADE_MS = 125;

// Nivel RMS do buffer de amostras. Media quadratica = energia do sinal, que
// e o que corresponde ao "volume percebido" melhor do que o pico.
export function calcularRms(amostras) {
  if (!amostras || amostras.length === 0) return 0;
  let soma = 0;
  for (let i = 0; i < amostras.length; i += 1) soma += amostras[i] * amostras[i];
  return Math.sqrt(soma / amostras.length);
}

// Sensibilidade da UI (0 a 100) -> limiar RMS. Invertido de proposito: mais
// sensibilidade = limiar menor = abre com som mais baixo.
export function limiarDaSensibilidade(sensibilidade) {
  const s = Math.min(Math.max(Number(sensibilidade) || 0, 0), 100) / 100;
  return LIMIAR_MAX - (LIMIAR_MAX - LIMIAR_MIN) * s;
}

// Um passo da maquina de estados do gate. Recebe a DECISAO ja tomada
// (`abrir`), nao o nivel bruto: quem decide e `pareceVoz`, que combina nivel e
// espectro. Assim a maquina de estados fica independente do criterio e da para
// simular uma conversa inteira em teste.
export function passoDoGate({ abrir, aberto, fechaEm, agora, holdMs = HOLD_MS }) {
  if (abrir) {
    // Som presente: abre (ou renova o hold).
    return { aberto: true, fechaEm: agora + holdMs };
  }
  if (aberto && agora >= fechaEm) {
    return { aberto: false, fechaEm: 0 };
  }
  // Abaixo do limiar mas ainda dentro do hold: segue aberto.
  return { aberto, fechaEm };
}

// Aproxima `atual` de `alvo`, andando o quanto couber no tempo DECORRIDO.
//
// O passo vem do tempo, nao do numero de tiques, de proposito: o navegador
// estrangula timers para ~1 tique/s quando a aba nao esta renderizando. Com
// passo fixo por tique o fade viraria uma rampa de varios segundos e o gate
// nunca chegaria a fechar de fato. Sendo por tempo, um tique atrasado
// simplesmente salta para o alvo.
export function aproximar(atual, alvo, decorridoMs = 25, duracaoMs = DURACAO_FADE_MS) {
  const passo = duracaoMs <= 0 ? 1 : decorridoMs / duracaoMs;
  if (Math.abs(alvo - atual) <= passo) return alvo;
  return atual + Math.sign(alvo - atual) * passo;
}

// Converte RMS em 0..1 para desenhar a barrinha de nivel. Escala logaritmica
// porque audicao e logaritmica — em escala linear a barra quase nao sai do
// canto durante uma fala normal.
export function nivelParaBarra(rms) {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  const normalizado = (db + 60) / 60; // -60 dB = 0, 0 dB = 1
  return Math.min(Math.max(normalizado, 0), 1);
}

// ---------------------------------------------------------------------------
// Analise espectral
//
// So o nivel nao distingue fala de barulho: um clique de teclado tem energia
// de sobra para abrir um gate por RMS. Duas medidas baratas resolvem boa parte
// disso, e as duas saem do mesmo AnalyserNode que ja media o nivel:
//
// 1. RAZAO NA BANDA DE VOZ — fala concentra energia entre 300 e 3400 Hz (a
//    banda telefonica, escolhida justamente por carregar a inteligibilidade).
//    Clique de teclado e transiente de banda larga, com muita energia acima de
//    4 kHz; ventilador e ar-condicionado empilham abaixo de 200 Hz. Os dois
//    tem razao baixa.
//
// 2. PLANICIDADE ESPECTRAL (entropia de Wiener) — ruido e plano ao longo do
//    espectro; voz sonora e "espetada", com harmonicos do fundamental. A razao
//    entre a media geometrica e a aritmetica das potencias fica perto de 1 no
//    ruido e perto de 0 no que e tonal.
//
// Nada disso e reconhecimento de voz. Teclado alto ENCOSTADO no microfone
// ainda passa: tem energia na banda de voz e o transiente e curto o bastante
// para escapar da media. O que isto resolve e o ruido de fundo constante e o
// clique distante.
// ---------------------------------------------------------------------------

// Banda telefonica. Nao comeca em 85 Hz (fundamental da voz masculina) de
// proposito: essa faixa e justamente onde ventilador e ar-condicionado moram,
// e a inteligibilidade nao depende dela.
export const VOZ_HZ_MIN = 300;
export const VOZ_HZ_MAX = 3400;

// Piso para nao tirar log de zero na media geometrica.
const EPSILON = 1e-12;

// dB (o que o AnalyserNode entrega) -> potencia linear, que e o que se pode
// somar. -Infinity dB vira 0.
export function potenciaPorBin(espectroDb) {
  const potencias = new Float64Array(espectroDb.length);
  for (let i = 0; i < espectroDb.length; i += 1) {
    potencias[i] = Number.isFinite(espectroDb[i]) ? 10 ** (espectroDb[i] / 10) : 0;
  }
  return potencias;
}

// Faixa de bins que cobre [deHz, ateHz]. Cada bin vale (taxa/2)/totalBins Hz.
export function binsDaBanda(deHz, ateHz, taxaAmostragem, totalBins) {
  const hzPorBin = (taxaAmostragem / 2) / totalBins;
  return {
    inicio: Math.max(0, Math.floor(deHz / hzPorBin)),
    fim: Math.min(totalBins - 1, Math.ceil(ateHz / hzPorBin)),
  };
}

// Quanto da energia total esta na banda de voz. 0 a 1.
export function razaoBandaDeVoz(potencias, taxaAmostragem) {
  let total = 0;
  for (let i = 0; i < potencias.length; i += 1) total += potencias[i];
  if (total <= 0) return 0;

  const { inicio, fim } = binsDaBanda(VOZ_HZ_MIN, VOZ_HZ_MAX, taxaAmostragem, potencias.length);
  let banda = 0;
  for (let i = inicio; i <= fim; i += 1) banda += potencias[i];
  return banda / total;
}

// Planicidade (media geometrica / media aritmetica) dentro da banda de voz.
// Perto de 1 = plano = ruido. Perto de 0 = espetado = tonal, tipico de voz.
// A geometrica sai por exp(media dos logs) para nao estourar o ponto
// flutuante multiplicando centenas de numeros minusculos.
export function planicidadeEspectral(potencias, taxaAmostragem) {
  const { inicio, fim } = binsDaBanda(VOZ_HZ_MIN, VOZ_HZ_MAX, taxaAmostragem, potencias.length);
  const n = fim - inicio + 1;
  if (n <= 1) return 1;

  let somaLogs = 0;
  let soma = 0;
  for (let i = inicio; i <= fim; i += 1) {
    const p = potencias[i] + EPSILON;
    somaLogs += Math.log(p);
    soma += p;
  }
  const geometrica = Math.exp(somaLogs / n);
  const aritmetica = soma / n;
  return aritmetica > 0 ? Math.min(geometrica / aritmetica, 1) : 1;
}

// Acima desta razao do limiar, o nivel decide sozinho e o espectro nao veta.
//
// Rede de seguranca deliberada: se os limiares espectrais estiverem mal
// calibrados para a voz de alguem, o pior caso vira "passa ruido demais" em
// vez de "a pessoa fica muda". Ficar mudo e o erro caro.
export const FATOR_CERTEZA = 3;

// CALIBRADOS COM MEDICAO, nao com palpite. Valores observados no navegador,
// atraves do codec Opus, com dois sinais de RMS IDENTICO:
//
//   serra harmonica (voz)  razao 0.29-0.36   planicidade 0.04
//   ruido branco (teclado) razao 0.15-0.18   planicidade 0.53-0.70
//
// A PLANICIDADE e o discriminador forte (13x de separacao) e carrega a
// decisao. A RAZAO entra como filtro fraco, para pegar o que e ruidoso mas
// concentrado fora da banda de voz — ventilador (0.003) e chiado agudo — que
// a planicidade sozinha deixaria passar.
//
// Os limiares ficam no meio da separacao medida, puxados para o lado
// permissivo: falso positivo deixa passar um ruido, falso negativo deixa uma
// PESSOA MUDA. O segundo erro e muito mais caro.
export const RAZAO_VOZ_MIN = 0.20;
export const PLANICIDADE_MAX = 0.40;

// Decisao final de abrir o gate. `razaoVoz` e `planicidade` sao opcionais —
// sem eles (ou sem espectro disponivel) cai no criterio antigo, so nivel.
export function pareceVoz({
  rms, limiar, razaoVoz = null, planicidade = null,
  razaoMin = RAZAO_VOZ_MIN, planicidadeMax = PLANICIDADE_MAX, fatorCerteza = FATOR_CERTEZA,
}) {
  if (rms < limiar) return false;
  if (rms >= limiar * fatorCerteza) return true;
  if (razaoVoz === null || planicidade === null) return true;
  return razaoVoz >= razaoMin && planicidade <= planicidadeMax;
}
